import React, { useState, useEffect, useRef, useCallback } from 'react';

// Billing row interface
export interface BillingRow {
  'S. No': number;
  'Medicine Name': string;
  'Composition': string;
  'Company': string;
  'Batch No': string;
  'Qty Type': string;
  'Quantity': number;
  'Unit Cost': number;
  'MRP': number;
  'GST%': number;
  'Discount': number;
  'HSN Code': string;
  'Storage': string;
  'Expiry Date': string; // YYYY-MM-DD format for date input
  'Tablets/Strip': number;
  'Total Tablets': number;
  'Selling Price': number;
  'Total': number;
  strip_base_selling_price?: number;
  strip_base_mrp?: number;
}

// Medicine suggestion interface (matching API response)
interface MedicineSuggestion {
  Name: string;
  Manufacturer: string;
  Salt_Composition: string;
  Quantity: string;
  Image_URL: string;
  key: string;
  relevance_score: number;
  fragmented_tablets?: number;
  // Full API response fields for auto-filling
  batch_no?: string;
  expiry_date?: string;
  mrp?: number;
  qty?: number;
  quantity?: number;
  quantity_type?: string;
  selling_price?: number;
  storage_location?: string;
  tablets_per_strip?: number;
  total_tablets?: number;
  unit_cost?: number;
  gst_percent?: number;
  gst_rate?: number;
  gst?: number | string;
  gst_percentage?: number;
  gst_slab?: number | string;
  // Store raw medicine data for reference
  rawData?: any;
}

// Quantity Type options
const QUANTITY_TYPES = ['Strip', 'Bottles', 'Tablet', 'Box', 'Tube', 'TAB', 'SYP', 'CAP', 'POWER', 'INJ', 'SACHET', 'LOTION', 'GEL', 'CREAM'];

// Conditional quantity type options based on current selection
const getConditionalQuantityTypes = (currentType: string): string[] => {
  if (currentType === 'Strip' || currentType === 'Tablet') {
    // Allow toggling between Strip and Tablet in both directions
    return ['Strip', 'Tablet'];
  } else {
    // If current type is not Strip, don't allow changing
    return [currentType];
  }
};

const isQtyTypeEditable = (currentType: string): boolean =>
  currentType === 'Strip' || currentType === 'Tablet';

// Storage options
const STORAGE_OPTIONS = ['Room Temperature', 'Refrigerated', 'Freezer', 'Cool & Dry Place'];

// Helper to normalize quantity type values from API to UI options
// If the quantity type is something we don't recognise (not in our dropdown),
// keep it as‑is instead of forcing it to "Strip".
const normalizeQtyType = (qtyType?: string): string => {
  const raw = (qtyType || '').trim();
  const value = raw.toLowerCase();

  switch (value) {
    case 'strip':
    case 'strips':
      return 'Strip';
    case 'bottle':
    case 'bottles':
      return 'Bottles';
    case 'tablet':
    case 'tablets':
    case 'individual':
      return 'Tablet';
    case 'box':
    case 'boxes':
      return 'Box';
    case 'tube':
    case 'tubes':
      return 'Tube';
    case 'tab':
    case 'tabs':
      return 'TAB';
    case 'syp':
    case 'syrup':
    case 'syrups':
      return 'SYP';
    case 'cap':
    case 'caps':
    case 'capsule':
    case 'capsules':
      return 'CAP';
    case 'power':
    case 'powder':
    case 'powders':
      return 'POWER';
    case 'inj':
    case 'injection':
    case 'injections':
      return 'INJ';
    case 'sachet':
    case 'sachets':
      return 'SACHET';
    case 'lotion':
    case 'lotions':
      return 'LOTION';
    case 'gel':
    case 'gels':
      return 'GEL';
    case 'cream':
    case 'creams':
      return 'CREAM';
    default:
      // For any custom / unknown type, just return it unchanged
      // (so billing can still submit with that value).
      return raw || 'Strip';
  }
};

// Helper to convert UI quantity type to API format
const toApiQtyType = (qtyType: string): string => {
  const value = qtyType || 'Strip';
  // Map quantity type from billing-tabel format to API format
  // billing-tabel uses: 'Strip', 'Bottles', 'Tablet', 'Box', 'Tube'
  // API expects: 'strip', 'bottle', 'individual', 'box', 'tube'
  // Note: 'Tablet' type should be sent as 'individual' in the API
  const qtyTypeMap: { [key: string]: string } = {
    'Strip': 'strip',
    'Bottles': 'bottle',
    'Bottle': 'bottle',
    'Tablet': 'individual',
    'Box': 'box',
    'Tube': 'tube',
    'Individual': 'individual',
    'TAB': 'tab',
    'SYP': 'syp',
    'CAP': 'cap',
    'POWER': 'power',
    'INJ': 'inj',
    'SACHET': 'sachet',
    'LOTION': 'lotion',
    'GEL': 'gel',
    'CREAM': 'cream'
  };
  // For known types, use our mapping. For anything else, send it exactly
  // as it appears in the UI so custom quantity types are preserved.
  return qtyTypeMap[value] || qtyType;
};

// Helper to safely parse numbers from API strings (e.g., "10 strips", "100 ml")
const parseNumericValue = (value: any): number => {
  if (typeof value === 'number') {
    return isNaN(value) ? 0 : value;
  }
  if (typeof value === 'string') {
    const sanitized = value.replace(/[^0-9.-]/g, '');
    const parsed = parseFloat(sanitized);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// Helper function to calculate Total
const calculateTotal = (
  sellingPrice: number,
  quantity: number,
  _discount: number,
  gstRate: number,
  qtyType?: string,
  tabletsPerStrip?: number
): number => {
  // Total should be computed strictly from selling price * quantity.
  // Discount is displayed/kept as separate field(s), but it must NOT reduce Total.
  const subtotal = sellingPrice * quantity;
  return Math.round(subtotal * 100) / 100; // Round to 2 decimals
};

// Helper function to calculate Total Tablets
const calculateTotalTablets = (quantity: number, tabletsPerStrip: number): number => {
  return quantity * tabletsPerStrip;
};

// Helper function to format currency
const formatCurrency = (value: number): string => {
  return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// GST is editable by user and can be filled from inventory
const NUMERIC_EDITABLE_FIELDS: (keyof BillingRow)[] = ['Quantity', 'Unit Cost', 'MRP', 'Discount', 'Selling Price', 'GST%'];

const handleDecimalInput = (value: string): string => {
  const cleaned = value.replace(/[^0-9.]/g, '');
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('');
  }
  return cleaned;
};

const roundToTwoDecimals = (value: number): number => Math.round(value * 100) / 100;

const getEffectiveRowPrice = (row: BillingRow): number => {
  const sellingPrice = Number(row['Selling Price']);
  if (!isNaN(sellingPrice) && sellingPrice > 0) {
    return sellingPrice;
  }

  const mrp = Number(row['MRP']);
  return isNaN(mrp) ? 0 : mrp;
};

const adjustValueForQtyTypeChange = (
  value: number,
  previousQtyType: string,
  newQtyType: string,
  tabletsPerStrip: number
): number => {
  if (!value || tabletsPerStrip <= 0) {
    return value;
  }

  const prevNormalized = normalizeQtyType(previousQtyType || 'Strip');
  const newNormalized = normalizeQtyType(newQtyType || 'Strip');

  if (prevNormalized === newNormalized) {
    return value;
  }

  if (prevNormalized === 'Tablet' && newNormalized !== 'Tablet') {
    return roundToTwoDecimals(value * tabletsPerStrip);
  }

  if (prevNormalized !== 'Tablet' && newNormalized === 'Tablet') {
    return roundToTwoDecimals(value / tabletsPerStrip);
  }

  return value;
};

// Main BillingTable component
interface BillingTableProps {
  onSubmitSuccess?: (message: string) => void;
  onSubmitError?: (message: string) => void;
  onRowsChange?: (rows: BillingRow[]) => void;
  globalDiscount?: number;
  onGlobalDiscountChange?: (value: number) => void;
  addMedicineData?: Partial<BillingRow> | null;
}

const BillingTable: React.FC<BillingTableProps> = ({
  onSubmitSuccess,
  onSubmitError,
  onRowsChange,
  globalDiscount: externalGlobalDiscount,
  onGlobalDiscountChange,
  addMedicineData
}) => {
  const [internalGlobalDiscount, setInternalGlobalDiscount] = useState<number>(0);
  const globalDiscount = externalGlobalDiscount !== undefined ? externalGlobalDiscount : internalGlobalDiscount;
  const [globalDiscountInput, setGlobalDiscountInput] = useState<string>('');
  const handleGlobalDiscountChange = (value: number) => {
    if (onGlobalDiscountChange) {
      onGlobalDiscountChange(value);
    }
    if (externalGlobalDiscount === undefined) {
      setInternalGlobalDiscount(value);
    }
  };
  useEffect(() => {
    if (externalGlobalDiscount !== undefined) {
      setGlobalDiscountInput(
        externalGlobalDiscount === 0 ? '' : String(externalGlobalDiscount)
      );
    }
  }, [externalGlobalDiscount]);

  const createDefaultRow = (serialNumber: number = 1): BillingRow => ({
    'S. No': serialNumber,
    'Medicine Name': '',
    'Composition': '',
    'Company': '',
    'Batch No': '',
    'Qty Type': 'Strip',
    'Quantity': 0,
    'Unit Cost': 0,
    'MRP': 0,
    'GST%': 0,
    'Discount': globalDiscount,
    'HSN Code': '',
    'Storage': 'Room Temperature',
    'Expiry Date': '',
    'Tablets/Strip': 0,
    'Total Tablets': 0,
    'Selling Price': 0,
    'Total': 0,
  });

  // Initialize with one default row
  const [rows, setRows] = useState<BillingRow[]>([createDefaultRow()]);
  
  // State for expiry date notification
  const [expiryNotification, setExpiryNotification] = useState<{
    show: boolean;
    message: string;
  }>({ show: false, message: '' });

  // Notify parent when rows change
  useEffect(() => {
    if (onRowsChange) {
      onRowsChange(rows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // Sync all rows with global discount when it changes
  useEffect(() => {
    setRows(prevRows => prevRows.map(row => ({
      ...row,
      'Discount': globalDiscount
    })));
  }, [globalDiscount]);

  // Helper function to check if expiry date is before today
  const isExpiryDateExpired = (expiryDate: string): boolean => {
    if (!expiryDate || expiryDate.trim() === '') return false;
    try {
      const expiry = new Date(expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
      expiry.setHours(0, 0, 0, 0);
      return expiry < today;
    } catch {
      return false;
    }
  };

  // Handle adding medicine data from prescription details
  useEffect(() => {
    if (addMedicineData) {
      console.log('billing-tabel received addMedicineData:', addMedicineData);
      // Check if expiry date is expired before adding
      if (addMedicineData['Expiry Date'] && isExpiryDateExpired(addMedicineData['Expiry Date'])) {
        const expiryDateStr = new Date(addMedicineData['Expiry Date']).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        const medicineName = addMedicineData['Medicine Name'] || 'this medicine';
        setExpiryNotification({
          show: true,
          message: `Cannot add ${medicineName}. Expiry date (${expiryDateStr}) has passed.`
        });
        // Auto-hide after 5 seconds
        setTimeout(() => {
          setExpiryNotification({ show: false, message: '' });
        }, 5000);
        return; // Prevent adding
      }

      setRows(prevRows => {
        // Check if the first row is empty (no medicine name, company, or composition)
        const firstRow = prevRows[0];
        const isFirstRowEmpty = !firstRow || 
          (!firstRow['Medicine Name']?.trim() && 
           !firstRow['Company']?.trim() && 
           !firstRow['Composition']?.trim());
        
        if (isFirstRowEmpty && prevRows.length > 0) {
          // Fill the first row instead of adding a new one
          const updatedRows = [...prevRows];
          const rowToFill = { ...updatedRows[0] };
          
          // Fill in the medicine data - check for undefined instead of truthy to allow empty strings
          if (addMedicineData['Medicine Name'] !== undefined) rowToFill['Medicine Name'] = addMedicineData['Medicine Name'];
          if (addMedicineData['Company'] !== undefined) rowToFill['Company'] = addMedicineData['Company'];
          if (addMedicineData['Composition'] !== undefined) rowToFill['Composition'] = addMedicineData['Composition'];
          if (addMedicineData['Batch No'] !== undefined) rowToFill['Batch No'] = addMedicineData['Batch No'];
          if (addMedicineData['Expiry Date'] !== undefined) rowToFill['Expiry Date'] = addMedicineData['Expiry Date'];
          if (addMedicineData['Quantity'] !== undefined) rowToFill['Quantity'] = addMedicineData['Quantity'];
          if (addMedicineData['Qty Type'] !== undefined) rowToFill['Qty Type'] = normalizeQtyType(addMedicineData['Qty Type']);
          if (addMedicineData['Tablets/Strip'] !== undefined) rowToFill['Tablets/Strip'] = addMedicineData['Tablets/Strip'];
          if (addMedicineData['Unit Cost'] !== undefined) rowToFill['Unit Cost'] = addMedicineData['Unit Cost'];
          if (addMedicineData['Selling Price'] !== undefined) rowToFill['Selling Price'] = addMedicineData['Selling Price'];
          if (addMedicineData['MRP'] !== undefined) rowToFill['MRP'] = addMedicineData['MRP'];
          if (addMedicineData['HSN Code'] !== undefined) rowToFill['HSN Code'] = addMedicineData['HSN Code'];
          if (addMedicineData['Storage'] !== undefined) rowToFill['Storage'] = addMedicineData['Storage'];
          if (addMedicineData['GST%'] !== undefined && addMedicineData['GST%'] !== null) rowToFill['GST%'] = Number(addMedicineData['GST%']);
          
          // Recalculate Total Tablets and Total
          rowToFill['Total Tablets'] = calculateTotalTablets(rowToFill['Quantity'] || 0, rowToFill['Tablets/Strip'] || 0);
          rowToFill['Total'] = calculateTotal(
            getEffectiveRowPrice(rowToFill),
            rowToFill['Quantity'] || 0,
            rowToFill['Discount'] || 0,
            rowToFill['GST%'] || 0,
            rowToFill['Qty Type'] || 'Strip',
            rowToFill['Tablets/Strip'] || 0
          );
          
          updatedRows[0] = rowToFill;
          return updatedRows;
        } else {
          // First row is not empty, add a new row
          const newRow = createDefaultRow(prevRows.length + 1);
          // Fill in the medicine data - check for undefined instead of truthy to allow empty strings
          if (addMedicineData['Medicine Name'] !== undefined) newRow['Medicine Name'] = addMedicineData['Medicine Name'];
          if (addMedicineData['Company'] !== undefined) newRow['Company'] = addMedicineData['Company'];
          if (addMedicineData['Composition'] !== undefined) newRow['Composition'] = addMedicineData['Composition'];
          if (addMedicineData['Batch No'] !== undefined) newRow['Batch No'] = addMedicineData['Batch No'];
          if (addMedicineData['Expiry Date'] !== undefined) newRow['Expiry Date'] = addMedicineData['Expiry Date'];
          if (addMedicineData['Quantity'] !== undefined) newRow['Quantity'] = addMedicineData['Quantity'];
          if (addMedicineData['Qty Type'] !== undefined) newRow['Qty Type'] = normalizeQtyType(addMedicineData['Qty Type']);
          if (addMedicineData['Tablets/Strip'] !== undefined) newRow['Tablets/Strip'] = addMedicineData['Tablets/Strip'];
          if (addMedicineData['Unit Cost'] !== undefined) newRow['Unit Cost'] = addMedicineData['Unit Cost'];
          if (addMedicineData['Selling Price'] !== undefined) newRow['Selling Price'] = addMedicineData['Selling Price'];
          if (addMedicineData['MRP'] !== undefined) newRow['MRP'] = addMedicineData['MRP'];
          if (addMedicineData['HSN Code'] !== undefined) newRow['HSN Code'] = addMedicineData['HSN Code'];
          if (addMedicineData['Storage'] !== undefined) newRow['Storage'] = addMedicineData['Storage'];
          if (addMedicineData['GST%'] !== undefined && addMedicineData['GST%'] !== null) newRow['GST%'] = Number(addMedicineData['GST%']);
          
          // Recalculate Total Tablets and Total
          newRow['Total Tablets'] = calculateTotalTablets(newRow['Quantity'] || 0, newRow['Tablets/Strip'] || 0);
          newRow['Total'] = calculateTotal(
            getEffectiveRowPrice(newRow),
            newRow['Quantity'] || 0,
            newRow['Discount'] || 0,
            newRow['GST%'] || 0,
            newRow['Qty Type'] || 'Strip',
            newRow['Tablets/Strip'] || 0
          );
          
          return [...prevRows, newRow];
        }
      });
    }
  }, [addMedicineData]);

  // Medicine search state
  const [medicineSuggestions, setMedicineSuggestions] = useState<{ [key: number]: MedicineSuggestion[] }>({});
  const [showDropdown, setShowDropdown] = useState<{ [key: number]: boolean }>({});
  const [activeDropdownRow, setActiveDropdownRow] = useState<number | null>(null);
  const [dropdownHeight, setDropdownHeight] = useState<number>(0);
  const [isSearching, setIsSearching] = useState<{ [key: number]: boolean }>({});
  const dropdownRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const tableScrollableRef = useRef<HTMLDivElement | null>(null);
  const inputRefs = useRef<{ [key: number]: HTMLInputElement | HTMLTextAreaElement | null }>({});
  const fieldRefs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null }>({});
  const [rowHeights, setRowHeights] = useState<{ [key: number]: number }>({});
  const searchTimeoutRef = useRef<{ [key: number]: NodeJS.Timeout | null }>({});
  const [numericDisplayValues, setNumericDisplayValues] = useState<{ [key: string]: string }>({});
  const [showTipBlock, setShowTipBlock] = useState(false);
  const handleAddField = useCallback(() => {
    setRows(prevRows => {
      const newRow = createDefaultRow(prevRows.length + 1);
      setRowHeights(prevHeights => ({ ...prevHeights, [prevRows.length]: 48 }));
      return [...prevRows, newRow];
    });
  }, []);

  const handleRemoveRow = useCallback((rowIndex: number) => {
    setRows(prevRows => {
      if (prevRows.length === 1) {
        setNumericDisplayValues({});
        setRowHeights({});
        return [createDefaultRow()];
      }
      const updatedRows = prevRows
        .filter((_, index) => index !== rowIndex)
        .map((row, idx) => ({
          ...row,
          'S. No': idx + 1
        }));
      setNumericDisplayValues({});
      return updatedRows;
    });
  }, []);

  // Search medicines function - calls API
  const searchMedicines = async (query: string, rowIndex: number): Promise<MedicineSuggestion[]> => {
    if (!query || query.trim().length === 0) {
      return [];
    }

    try {
      // Get auth token from localStorage
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        console.error('Authentication token not found');
        return [];
      }

      // Construct API URL
      const apiUrl = `https://pharma-amd-0-1-42.onrender.com/pharma/api/inventory/search?q=${encodeURIComponent(query)}&limit=10`;

      // Call the inventory search API
      const response = await fetch(apiUrl, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        console.error('Failed to search medicines:', response.status);
        return [];
      }

      const data = await response.json();
      
      // Handle different possible response formats
      let medicines: any[] = [];
      if (data.inventory && Array.isArray(data.inventory)) {
        medicines = data.inventory;
      } else if (Array.isArray(data)) {
        medicines = data;
      } else if (data.medicines && Array.isArray(data.medicines)) {
        medicines = data.medicines;
      } else if (data.results && Array.isArray(data.results)) {
        medicines = data.results;
      } else if (data.data && Array.isArray(data.data)) {
        medicines = data.data;
      }

      // Map API response to MedicineSuggestion format
      // API returns: medicine_name, medicine_composition, medicine_company, and other fields
      const mappedMedicines = medicines.map((medicine: any) => ({
        Name: medicine.medicine_name || medicine.name || medicine.Name || '',
        Manufacturer: medicine.medicine_company || medicine.manufacturer || medicine.Manufacturer || '',
        Salt_Composition: medicine.medicine_composition || medicine.salt_composition || medicine.Salt_Composition || '',
        Quantity: medicine.quantity || medicine.qty || medicine.Quantity || '',
        Image_URL: medicine.Image_URL || medicine.image_url || medicine.image || '',
        key: medicine.id || medicine.key || `medicine-${medicine.medicine_name || medicine.name || Date.now()}-${rowIndex}`,
        relevance_score: medicine.relevance_score || medicine.score || 0,
        // Store all API fields for auto-filling
        batch_no: medicine.batch_no || '',
        expiry_date: medicine.expiry_date || '',
        mrp: medicine.mrp || 0,
        qty: medicine.qty || medicine.quantity || 0,
        quantity: medicine.quantity || medicine.qty || 0,
        quantity_type: medicine.quantity_type || 'Strip',
        selling_price: medicine.selling_price || 0,
        storage_location: medicine.storage_location || 'Room Temperature',
        tablets_per_strip: medicine.tablets_per_strip || 0,
        total_tablets: medicine.total_tablets || 0,
        unit_cost: medicine.unit_cost || 0,
        fragmented_tablets: medicine.fragmented_tablets ?? 0,
        gst_percent: medicine.gst_percent ?? medicine.gst_rate ?? medicine.gst ?? medicine.gst_percentage ?? medicine.gst_slab ?? undefined,
        // Store raw data for any additional fields
        rawData: medicine
      }));

      return mappedMedicines;
    } catch (error: any) {
      console.error('Error searching medicines:', error);
      return [];
    }
  };

  // Handle medicine name change with autocomplete
  const handleMedicineNameChange = (rowIndex: number, value: string) => {
    handleCellChange(rowIndex, 'Medicine Name', value);
    
    // Clear previous timeout
    if (searchTimeoutRef.current[rowIndex]) {
      clearTimeout(searchTimeoutRef.current[rowIndex]);
    }

    // If value is empty, clear suggestions and hide dropdown
    if (!value || value.trim().length === 0) {
      setMedicineSuggestions(prev => ({
        ...prev,
        [rowIndex]: []
      }));
      setShowDropdown(prev => ({
        ...prev,
        [rowIndex]: false
      }));
      setIsSearching(prev => ({
        ...prev,
        [rowIndex]: false
      }));
      setActiveDropdownRow(null);
      return;
    }

    // Set active dropdown row immediately so dropdown can show
    setActiveDropdownRow(rowIndex);

    // Set loading state and show dropdown
    setIsSearching(prev => ({
      ...prev,
      [rowIndex]: true
    }));
    setShowDropdown(prev => ({
      ...prev,
      [rowIndex]: true
    }));

    // Debounce API call (wait 300ms after user stops typing)
    searchTimeoutRef.current[rowIndex] = setTimeout(async () => {
      const suggestions = await searchMedicines(value, rowIndex);
      
      setMedicineSuggestions(prev => ({
        ...prev,
        [rowIndex]: suggestions
      }));
      
      // Keep dropdown open to show results or "No medicines found" message
      setShowDropdown(prev => ({
        ...prev,
        [rowIndex]: true // Always show dropdown after search completes (will show "No medicines found" if empty)
      }));
      
      setIsSearching(prev => ({
        ...prev,
        [rowIndex]: false
      }));
      
      // Keep activeDropdownRow set
      setActiveDropdownRow(rowIndex);
    }, 300);
  };

  // Handle medicine selection from dropdown
  const handleMedicineSelect = (rowIndex: number, medicine: MedicineSuggestion) => {
    // Check if expiry date is expired before adding
    if (medicine.expiry_date && isExpiryDateExpired(medicine.expiry_date)) {
      const expiryDateStr = new Date(medicine.expiry_date).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      setExpiryNotification({
        show: true,
        message: `Cannot select ${medicine.Name || 'this medicine'}. Expiry date (${expiryDateStr}) has passed.`
      });
      // Auto-hide after 5 seconds
      setTimeout(() => {
        setExpiryNotification({ show: false, message: '' });
      }, 5000);
      return; // Prevent selection
    }

    const updatedRows = [...rows];
    const row = { ...updatedRows[rowIndex] };
    
    // Auto-fill all fields from selected medicine
    row['Medicine Name'] = medicine.Name || '';
    row['Company'] = medicine.Manufacturer || '';
    row['Composition'] = medicine.Salt_Composition || '';
    row['Batch No'] = medicine.batch_no || '';
    row['Expiry Date'] = medicine.expiry_date || '';
    row['Qty Type'] = normalizeQtyType(medicine.quantity_type);
    // Quantity field is NOT autofilled - user must enter it manually
    row['Unit Cost'] = parseNumericValue(medicine.unit_cost);
    row['MRP'] = parseNumericValue(medicine.mrp);
    row['Selling Price'] = parseNumericValue(medicine.selling_price);
    // GST auto-fill from selected medicine (inventory/API)
    const gstFromApi = medicine.gst_percent ?? medicine.gst_rate ?? medicine.gst ?? medicine.gst_percentage ?? medicine.gst_slab
      ?? medicine.rawData?.gst_percent ?? medicine.rawData?.gst_rate ?? medicine.rawData?.gst ?? medicine.rawData?.gst_percentage;
    if (gstFromApi !== undefined && gstFromApi !== null && gstFromApi !== '') {
      row['GST%'] = parseNumericValue(gstFromApi);
    } else if (row['GST%'] === undefined || row['GST%'] === null) {
      row['GST%'] = 0;
    }
    row['Storage'] = medicine.storage_location || 'Room Temperature';
    row['Tablets/Strip'] = parseNumericValue(medicine.tablets_per_strip);
    
    // Recalculate Total Tablets and Total (using existing quantity if any, otherwise 0)
    row['Total Tablets'] = calculateTotalTablets(row['Quantity'] || 0, row['Tablets/Strip'] || 0);
    row['Total'] = calculateTotal(
      getEffectiveRowPrice(row), 
      row['Quantity'] || 0, 
      row['Discount'] || 0, 
      row['GST%'] || 0,
      row['Qty Type'] || 'Strip',
      row['Tablets/Strip'] || 0
    );
    
    updatedRows[rowIndex] = row;
    setRows(updatedRows);
    
    // Hide dropdown
    setShowDropdown(prev => ({
      ...prev,
      [rowIndex]: false
    }));
    setActiveDropdownRow(null);
    setDropdownHeight(0);
  };

  // Update dropdown height when it's shown to expand container
  useEffect(() => {
    if (activeDropdownRow !== null && showDropdown[activeDropdownRow]) {
      // Fixed height for 3 items display (270px)
      setDropdownHeight(270);
    } else {
      setDropdownHeight(0);
    }
  }, [activeDropdownRow, showDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdownRow !== null) {
        const target = event.target as HTMLElement;
        const inputRef = inputRefs.current[activeDropdownRow];
        const dropdownRef = dropdownRefs.current[activeDropdownRow];
        
        // Check if click is on dropdown item or dropdown container (allow it)
        const isDropdownClick = target.closest('[data-dropdown-item]');
        if (isDropdownClick) {
          return;
        }
        
        // Check if click is on the dropdown container itself
        if (dropdownRef && dropdownRef.contains(target)) {
          return;
        }
        
        // Check if click is on the input field (allow it)
        if (inputRef && inputRef.contains(target)) {
          return;
        }
        
        // Close dropdown if click is outside
        setShowDropdown(prev => ({
          ...prev,
          [activeDropdownRow]: false
        }));
        setActiveDropdownRow(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdownRow]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(searchTimeoutRef.current).forEach(timeout => {
        if (timeout) {
          clearTimeout(timeout);
        }
      });
    };
  }, []);

  // Keyboard shortcuts: 
  // Ctrl/Cmd + A: Add new row
  // Ctrl/Cmd + D: Delete last row
  // Ctrl/Cmd + Right/Left Arrow: Scroll table horizontally
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
      if (event.ctrlKey || event.metaKey) {
        // Only work on desktop (not mobile)
        if (window.innerWidth < 768) return;

        const key = event.key.toLowerCase();

        // Ctrl+A: Add new row
        if (key === 'a') {
          event.preventDefault();
          event.stopPropagation();
          handleAddField();
          return;
        }

        // Ctrl+D: Delete last row
        if (key === 'd') {
          event.preventDefault();
          event.stopPropagation();
          if (rows.length > 0) {
            handleRemoveRow(rows.length - 1);
          }
          return;
        }

        // Ctrl+Right/Left Arrow: Scroll table horizontally
        if (key === 'arrowright' || key === 'arrowleft') {
          // Try to scroll the inner scrollable div first, then the outer container
          const scrollAmount = 300;
          let scrolled = false;

          // Try inner scrollable div
          if (tableScrollableRef.current) {
            const currentScroll = tableScrollableRef.current.scrollLeft;
            const maxScroll = tableScrollableRef.current.scrollWidth - tableScrollableRef.current.clientWidth;
            
            if (key === 'arrowright' && currentScroll < maxScroll) {
              event.preventDefault();
              event.stopPropagation();
              const targetScroll = Math.min(currentScroll + scrollAmount, maxScroll);
              tableScrollableRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
              });
              scrolled = true;
            } else if (key === 'arrowleft' && currentScroll > 0) {
              event.preventDefault();
              event.stopPropagation();
              const targetScroll = Math.max(currentScroll - scrollAmount, 0);
              tableScrollableRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
              });
              scrolled = true;
            }
          }

          // If inner div didn't scroll, try outer container
          if (!scrolled && tableContainerRef.current) {
            const currentScroll = tableContainerRef.current.scrollLeft;
            const maxScroll = tableContainerRef.current.scrollWidth - tableContainerRef.current.clientWidth;
            
            if (key === 'arrowright' && currentScroll < maxScroll) {
              event.preventDefault();
              event.stopPropagation();
              const targetScroll = Math.min(currentScroll + scrollAmount, maxScroll);
              tableContainerRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
              });
            } else if (key === 'arrowleft' && currentScroll > 0) {
              event.preventDefault();
              event.stopPropagation();
              const targetScroll = Math.max(currentScroll - scrollAmount, 0);
              tableContainerRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
              });
            }
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleAddField, handleRemoveRow, rows.length]);

  // Update a specific field in a row
  const handleCellChange = (rowIndex: number, field: keyof BillingRow, value: string | number) => {
    // GST% is now editable

    const updatedRows = [...rows];
    const row = { ...updatedRows[rowIndex] };
    
    // Convert value to appropriate type
    const previousQtyType = row['Qty Type'];

    if (field === 'Tablets/Strip') {
      // Tablets per strip is auto-filled and read-only
      return;
    } else if (NUMERIC_EDITABLE_FIELDS.includes(field)) {
      const stringValue = String(value);
      const fieldKey = `${rowIndex}-${field}`;
      setNumericDisplayValues(prev => ({ ...prev, [fieldKey]: stringValue }));
      const numValue = stringValue.trim() === '' ? 0 : Number(stringValue);
      let parsedValue = isNaN(numValue) ? 0 : numValue;
      if (field === 'Discount') {
        parsedValue = Math.max(parsedValue, 0);
      }
      (row[field] as number) = parsedValue;
    } else if (field === 'Total Tablets' || field === 'Total') {
      // These are calculated fields, don't update directly
      return;
    } else if (field === 'Qty Type') {
      const normalizedQtyType = normalizeQtyType(String(value));
      row['Qty Type'] = normalizedQtyType;

      const tabletsPerStrip = row['Tablets/Strip'] || 0;
      if (previousQtyType && previousQtyType !== normalizedQtyType) {
        const previousNormalizedQtyType = normalizeQtyType(previousQtyType);
        const switchingToTablet =
          previousNormalizedQtyType !== 'Tablet' && normalizedQtyType === 'Tablet';
        const switchingFromTablet =
          previousNormalizedQtyType === 'Tablet' && normalizedQtyType !== 'Tablet';

        // Persist strip-equivalent values so API submission can always use strip amount.
        if (switchingToTablet) {
          row.strip_base_selling_price = row['Selling Price'] || 0;
          row.strip_base_mrp = row['MRP'] || 0;
        }

        const updatedUnitCost = adjustValueForQtyTypeChange(
          row['Unit Cost'] || 0,
          previousQtyType,
          normalizedQtyType,
          tabletsPerStrip
        );
        const updatedMrp = adjustValueForQtyTypeChange(
          row['MRP'] || 0,
          previousQtyType,
          normalizedQtyType,
          tabletsPerStrip
        );
        const updatedSellingPrice = adjustValueForQtyTypeChange(
          row['Selling Price'] || 0,
          previousQtyType,
          normalizedQtyType,
          tabletsPerStrip
        );
        row['Unit Cost'] = updatedUnitCost;
        row['MRP'] = updatedMrp;
        row['Selling Price'] = updatedSellingPrice;

        if (switchingFromTablet) {
          row.strip_base_selling_price = updatedSellingPrice;
          row.strip_base_mrp = updatedMrp;
        }

        setNumericDisplayValues(prev => {
          const newValues = { ...prev };
          ['Unit Cost', 'MRP', 'Selling Price'].forEach((dependentField) => {
            const dependentKey = `${rowIndex}-${dependentField}`;
            if (newValues[dependentKey] !== undefined) {
              delete newValues[dependentKey];
            }
          });
          return newValues;
        });
      }

      ['Quantity'].forEach((dependentField) => {
        const dependentKey = `${rowIndex}-${dependentField}`;
        setNumericDisplayValues(prev => {
          if (prev[dependentKey] === undefined) return prev;
          const newValues = { ...prev };
          delete newValues[dependentKey];
          return newValues;
        });
        (row[dependentField as keyof BillingRow] as number) = 0;
      });
    } else if (field !== 'S. No') {
      (row[field] as string) = String(value);
      const fieldKey = `${rowIndex}-${field}`;
      setNumericDisplayValues(prev => {
        if (!prev[fieldKey]) return prev;
        const newValues = { ...prev };
        delete newValues[fieldKey];
        return newValues;
      });
    }
    
    // Recalculate Total Tablets when Quantity changes
    // Note: Tablets/Strip is read-only and handled earlier, so we only check Quantity here
    if (field === 'Quantity') {
      row['Total Tablets'] = calculateTotalTablets(row['Quantity'], row['Tablets/Strip']);
    }
    
    // Recalculate Total when Qty Type changes (to apply Tablet calculation if needed)
    if (field === 'Qty Type') {
      // Force recalculation when quantity type changes
    }
    
    // Always recalculate Total whenever relevant fields change
    row['Total'] = calculateTotal(
      getEffectiveRowPrice(row), 
      row['Quantity'] || 0, 
      row['Discount'] || 0,
      row['GST%'] || 0,
      row['Qty Type'] || 'Strip',
      row['Tablets/Strip'] || 0
    );
    
    // Update S. No for all rows
    updatedRows[rowIndex] = row;
    updatedRows.forEach((r, idx) => {
      r['S. No'] = idx + 1;
    });
    
    setRows(updatedRows);
  };

  // Calculate total value
  const calculateTotalValue = (): number => {
    return rows.reduce((sum, row) => sum + (row['Total'] || 0), 0);
  };

  // Handle submit
  const handleSubmit = async () => {
    try {
      // Get auth token from localStorage
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        alert('Authentication token not found. Please log in again.');
        return;
      }

      // Filter out empty rows and prepare data for API
      const validRows = rows.filter(row => 
        row['Medicine Name'].trim() !== '' && 
        row['Company'].trim() !== '' && 
        row['Composition'].trim() !== '' &&
        row['Batch No'].trim() !== '' &&
        row['Quantity'] > 0 &&
        getEffectiveRowPrice(row) > 0
      );

      if (validRows.length === 0) {
        alert('Please fill in at least one complete row with all required fields.');
        return;
      }

      // Prepare billing data according to API format
      const items = validRows.map((row) => {
        // Get GST rate from row (editable by user)
        const gstRate = row['GST%'] || 0;
        
        // Ensure discount is always a valid number
        const discountValue = row['Discount'];
        const discountPercent = (discountValue !== undefined && discountValue !== null && !isNaN(Number(discountValue)))
          ? parseFloat(String(discountValue))
          : 0.0;
        
        // UI can show tablet-level price, but API should always receive strip-equivalent amount.
        const displaySellingPrice = parseFloat(String(getEffectiveRowPrice(row))) || 0.00;
        const normalizedQtyType = normalizeQtyType(row['Qty Type']);
        const apiQtyType = toApiQtyType(row['Qty Type']);
        const tabletsPerStrip = row['Tablets/Strip'] || 0;
        const stripSellingPriceFromBase = Number(row.strip_base_selling_price || 0);
        const sellingPrice =
          apiQtyType === 'individual'
            ? roundToTwoDecimals(
                stripSellingPriceFromBase > 0
                  ? stripSellingPriceFromBase
                  : normalizedQtyType === 'Tablet' && tabletsPerStrip > 0
                    ? displaySellingPrice * tabletsPerStrip
                    : displaySellingPrice
              )
            : displaySellingPrice;
        const quantity = row['Quantity'] || 0;
        
        return {
          medicine_name: row['Medicine Name'].trim(),
          batch_no: row['Batch No'].trim(),
          quantity: quantity,
          quantity_type: toApiQtyType(row['Qty Type']),
          discount_percent: discountPercent,
          gst_percent: gstRate,
          selling_price: sellingPrice
        };
      });

      // Calculate total amount from all items
      const totalAmount = validRows.reduce((sum, row) => sum + (row['Total'] || 0), 0);

      // Calculate average GST rate from items (or use 0 if no items have GST)
      const topLevelGstRate = validRows.length > 0 
        ? validRows.reduce((sum, row) => sum + (row['GST%'] || 0), 0) / validRows.length 
        : 0;

      // Prepare final billing data with top-level fields
      const billingData = {
        items: items,
        amount_paid: totalAmount, // You may want to make this configurable
        payment_mode: "cash", // You may want to make this configurable
        customer_name: "", // You may want to make this configurable or get from props
        gst_percent: topLevelGstRate,
        discount_percent: globalDiscount || 0
      };

      console.log('Submitting billing data:', billingData);

      // TODO: Replace with actual billing API endpoint
      // const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/billing/', {
      //   method: 'POST',
      //   mode: 'cors',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'ngrok-skip-browser-warning': 'true',
      //     'Authorization': `Bearer ${authToken}`
      //   },
      //   body: JSON.stringify(billingData)
      // });

      // For now, just show success message
      const successMessage = `Successfully submitted ${validRows.length} item(s)!`;
      
      if (onSubmitSuccess) {
        onSubmitSuccess(successMessage);
      } else {
        alert(successMessage);
      }
      
    } catch (error: any) {
      console.error('Error submitting billing:', error);
      const errorMessage = `Error: ${error.message || 'Unknown error'}`;
      
      if (onSubmitError) {
        onSubmitError(errorMessage);
      } else {
        alert(errorMessage);
      }
    }
  };

  // Column definitions in exact order - defined early so it's accessible in updateRowHeight
  // Order: S. No, Medicine Name, Batch No, Expiry Date, Qty Type, Quantity, Tablets/Strip, Total Tablets, MRP, Selling Price, Unit Cost, GST%, Storage, Total (Actions is separate)
  const columns: (keyof BillingRow)[] = [
    'S. No',
    'Medicine Name',
    'Batch No',
    'Expiry Date',
    'Qty Type',
    'Quantity',
    'Tablets/Strip',
    'Total Tablets',
    'MRP',
    'Selling Price',
    'Unit Cost',
    'GST%',
    'Storage',
    'Total',
  ];

  // Helper function to auto-resize textarea and update row height
  const autoResizeTextarea = (textarea: HTMLTextAreaElement, rowIndex: number, updateRowHeight: (rowIndex: number, height: number) => void) => {
    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight;
    const minHeight = 48; // Minimum height in pixels (matching input fields)
    const maxHeight = 200; // Maximum height in pixels
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
    
    // Update row height to sync all fields in the row
    updateRowHeight(rowIndex, newHeight);
  };

  // Function to update row height and sync all fields in the row
  const updateRowHeight = (rowIndex: number, height: number) => {
    setRowHeights(prev => {
      const currentHeight = prev[rowIndex] || 48;
      const newHeight = Math.max(currentHeight, height);
      if (newHeight !== currentHeight) {
        // Update all fields in the row to match the tallest field
        setTimeout(() => {
          columns.forEach((column) => {
            const fieldKey = `${rowIndex}-${column}`;
            const field = fieldRefs.current[fieldKey];
            if (field) {
              // Set height for all field types
              field.style.height = `${newHeight}px`;
              if (field instanceof HTMLTextAreaElement) {
                // For textareas, handle overflow
                field.style.overflowY = field.scrollHeight > newHeight ? 'auto' : 'hidden';
              } else if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) {
                // For inputs and selects, ensure height is set
                field.style.height = `${newHeight}px`;
                field.style.minHeight = `${newHeight}px`;
              }
            }
          });
        }, 0);
        return { ...prev, [rowIndex]: newHeight };
      }
      return prev;
    });
  };

  // Initialize row heights for all rows on mount and when rows change
  useEffect(() => {
    const initialHeights: { [key: number]: number } = {};
    rows.forEach((_, index) => {
      if (!rowHeights[index]) {
        initialHeights[index] = 48;
      }
    });
    if (Object.keys(initialHeights).length > 0) {
      setRowHeights(prev => ({ ...prev, ...initialHeights }));
    }
  }, [rows.length]); // Only run when number of rows changes

  // Effect to sync all field heights when rowHeights changes or rows change
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Sync all fields in all rows to match their row height
      rows.forEach((_, rowIndex) => {
        const height = rowHeights[rowIndex] || 48; // Default to 48px if no height set
        columns.forEach((column) => {
          // Skip S. No, Total, and Total Tablets as they're read-only displays
          if (column === 'S. No' || column === 'Total' || column === 'Total Tablets') return;
          
          const fieldKey = `${rowIndex}-${column}`;
          const field = fieldRefs.current[fieldKey];
          if (field) {
            // Set height for all field types - force same height regardless of content
            field.style.height = `${height}px`;
            if (field instanceof HTMLTextAreaElement) {
              // For textareas, handle overflow but maintain height
              field.style.overflowY = field.scrollHeight > height ? 'auto' : 'hidden';
              field.style.minHeight = `${height}px`;
              field.style.maxHeight = `${height}px`;
            } else if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) {
              // For inputs and selects, ensure height, minHeight, and maxHeight are all set
              field.style.height = `${height}px`;
              field.style.minHeight = `${height}px`;
              field.style.maxHeight = `${height}px`;
            }
          }
        });
      });
    });
  }, [rowHeights, rows]);

  // Filter out Composition and Company from display columns
  const displayColumns = columns.filter(col => col !== 'Composition' && col !== 'Company');

  // Columns that should be right-aligned (numeric fields)
  const rightAlignColumns: (keyof BillingRow)[] = [
    'S. No',
    'Quantity',
    'Unit Cost',
    'MRP',
    'GST%',
    'Tablets/Strip',
    'Total Tablets',
    'Selling Price',
    'Total',
  ];

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{
      width: '100%',
      maxWidth: isMobile ? '100%' : '100%',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem',
      margin: 0,
      padding: 0,
      alignItems: isMobile ? 'center' : 'stretch'
    }}>
      {/* Expiry Date Notification */}
      {expiryNotification.show && (
        <div style={{
          position: 'fixed',
          top: isMobile ? '10px' : '20px',
          right: isMobile ? '10px' : '20px',
          left: isMobile ? '10px' : 'auto',
          zIndex: 10000,
          background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
          border: '2px solid #ef4444',
          color: '#991b1b',
          padding: isMobile ? '0.875rem 1rem' : '1rem 1.5rem',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(239, 68, 68, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '0.5rem' : '0.75rem',
          minWidth: isMobile ? 'auto' : '350px',
          maxWidth: isMobile ? 'calc(100% - 20px)' : '500px',
          width: isMobile ? 'calc(100% - 20px)' : 'auto',
          animation: 'slideInRight 0.3s ease-out',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: 'rgba(239, 68, 68, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v4M12 17h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/>
            </svg>
          </div>
          <div style={{ 
            flex: 1, 
            fontSize: isMobile ? '0.875rem' : '0.9375rem', 
            fontWeight: '600',
            lineHeight: '1.4'
          }}>
            {expiryNotification.message}
          </div>
          <button
            onClick={() => setExpiryNotification({ show: false, message: '' })}
            style={{
              background: 'rgba(239, 68, 68, 0.2)',
              border: 'none',
              borderRadius: '6px',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.2s ease',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
              }
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <style>{`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}</style>
        </div>
      )}

      {/* Add Field Button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: isMobile ? 'center' : 'flex-end',
        gap: '1rem',
        flexWrap: 'wrap',
        marginRight: isMobile ? '0' : '1.5rem',
        width: isMobile ? '100%' : 'auto',
        padding: isMobile ? '0 0.5rem' : '0',
        boxSizing: 'border-box'
      }}>
        {rows.length > 0 && (
          <div style={{
            fontSize: '0.875rem',
            color: '#6b7280',
            fontWeight: '500'
          }}>
            {rows.length} {rows.length === 1 ? 'item' : 'items'} added
          </div>
        )}
        <button
          onClick={() => setShowTipBlock(!showTipBlock)}
          type="button"
          style={{
            background: showTipBlock ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : 'transparent',
            border: showTipBlock ? 'none' : '1px solid #d1d5db',
            color: showTipBlock ? 'white' : '#6b7280',
            cursor: 'pointer',
            padding: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: isMobile ? '32px' : '36px',
            height: isMobile ? '32px' : '36px',
            borderRadius: '8px',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            if (!showTipBlock) {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
              e.currentTarget.style.color = '#1f2937';
            }
          }}
          onMouseLeave={(e) => {
            if (!showTipBlock) {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#6b7280';
            }
          }}
          title="Keyboard Shortcuts"
        >
          <svg width={isMobile ? "18" : "20"} height={isMobile ? "18" : "20"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 16v-4M12 8h.01"/>
          </svg>
        </button>
        <button
          onClick={handleAddField}
          type="button"
          style={{
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: isMobile ? '0.75rem 1.25rem' : '0.875rem 1.5rem',
            fontSize: isMobile ? '0.875rem' : '0.9375rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
            outline: 'none'
          }}
          onMouseEnter={(e) => {
            if (e.currentTarget) {
              e.currentTarget.style.background = 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            if (e.currentTarget) {
              e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
            }
          }}
          onFocus={(e) => {
            if (e.currentTarget) {
              e.currentTarget.style.outline = '2px solid rgba(239, 68, 68, 0.5)';
              e.currentTarget.style.outlineOffset = '2px';
            }
          }}
          onBlur={(e) => {
            if (e.currentTarget) {
              e.currentTarget.style.outline = 'none';
            }
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          <span>Add Field</span>
        </button>
      </div>

      {/* Tip Block */}
      {showTipBlock && (
        <div style={{
          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          border: '1px solid #93c5fd',
          borderRadius: '12px',
          padding: isMobile ? '1rem' : '1.5rem',
          marginBottom: '1.5rem',
          marginLeft: isMobile ? '0.5rem' : '1.5rem',
          marginRight: isMobile ? '0.5rem' : '1.5rem',
          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '0.75rem'
          }}>
            <h3 style={{
              fontSize: isMobile ? '1rem' : '1.125rem',
              fontWeight: '700',
              color: '#1e40af',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
              Keyboard Shortcuts
            </h3>
            <button
              onClick={() => setShowTipBlock(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#6b7280',
                cursor: 'pointer',
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '0.75rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '8px',
              padding: '0.75rem',
              border: '1px solid #bfdbfe'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.25rem'
              }}>
                <kbd style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  Ctrl
                </kbd>
                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>+</span>
                <kbd style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  A
                </kbd>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                Add New Row
              </div>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '8px',
              padding: '0.75rem',
              border: '1px solid #bfdbfe'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.25rem'
              }}>
                <kbd style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  Ctrl
                </kbd>
                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>+</span>
                <kbd style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  D
                </kbd>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                Delete Last Row
              </div>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '8px',
              padding: '0.75rem',
              border: '1px solid #bfdbfe'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.25rem',
                flexWrap: 'wrap'
              }}>
                <kbd style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  Ctrl
                </kbd>
                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>+</span>
                <kbd style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  ←
                </kbd>
                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>/</span>
                <kbd style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  →
                </kbd>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                Scroll Table Horizontally
              </div>
            </div>
          </div>
          <div style={{
            marginTop: '0.75rem',
            paddingTop: '0.75rem',
            borderTop: '1px solid #bfdbfe',
            fontSize: '0.75rem',
            color: '#6b7280',
            fontStyle: 'italic'
          }}>
            💡 Tip: These shortcuts work on desktop only
          </div>
        </div>
      )}

      {/* Mobile Card Layout */}
      {isMobile ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          width: '100%',
          maxWidth: '100%',
          padding: '0 0.5rem',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {rows.length === 0 ? (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '3rem 1rem',
              textAlign: 'center',
              color: '#6b7280',
              border: '1px solid #e5e7eb'
            }}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" style={{ margin: '0 auto 1rem' }}>
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '500', color: '#6b7280' }}>
                No rows added yet
              </p>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#9ca3af' }}>
                Click "Add Field" to start adding billing items
              </p>
            </div>
          ) : (
            rows.map((row, rowIndex) => (
              <div
                key={rowIndex}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  padding: '1rem',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  position: 'relative',
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box'
                }}
              >
                {/* S. No Badge */}
                <div style={{
                  position: 'absolute',
                  top: '0.75rem',
                  right: '0.75rem',
                  background: '#f3f4f6',
                  color: '#4b5563',
                  fontWeight: '600',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '6px',
                  fontSize: '0.75rem'
                }}>
                  #{row['S. No']}
                </div>

                {/* Field Name-Value Pairs */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                  marginTop: '0.5rem'
                }}>
                  {displayColumns.filter(col => col !== 'S. No').map((column) => {
                    const value = row[column];
                    const isReadOnly = column === 'Total' || column === 'Total Tablets';
                    const isRightAligned = rightAlignColumns.includes(column);

                    const inputStyle: React.CSSProperties = {
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      background: isReadOnly ? '#f3f4f6' : 'white',
                      color: '#111827',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                      margin: 0,
                      minHeight: '48px',
                      height: 'auto'
                    };

                    return (
                      <div
                        key={column}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.5rem 0',
                          borderBottom: column !== 'Total' ? '1px solid #f3f4f6' : 'none'
                        }}
                      >
                        {/* Field Name - Left Side */}
                        <div style={{
                          flex: '0 0 40%',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: '#6b7280',
                          textTransform: 'capitalize'
                        }}>
                          {column}:
                        </div>

                        {/* Field Value - Right Side */}
                        <div style={{
                          flex: '1 1 60%',
                          display: 'flex',
                          justifyContent: isRightAligned ? 'flex-end' : 'flex-start'
                        }}>
                          {column === 'Total' || column === 'Total Tablets' ? (
                            <span style={{
                              fontWeight: '700',
                              color: column === 'Total' ? '#ef4444' : '#10b981',
                              fontSize: '0.9375rem'
                            }}>
                              {column === 'Total' ? formatCurrency(Number(value)) : value}
                            </span>
                          ) : column === 'Tablets/Strip' ? (
                            <span
                              style={{
                                fontWeight: '600',
                                color: '#1f2937',
                                fontSize: '0.9rem'
                              }}
                            >
                              {value || 0}
                            </span>
                          ) : column === 'Qty Type' ? (
                            <select
                              ref={(el) => { fieldRefs.current[`${rowIndex}-${column}`] = el; }}
                              value={String(value)}
                              onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                              disabled={!isQtyTypeEditable(String(value))}
                              style={{
                                ...inputStyle,
                                cursor: isQtyTypeEditable(String(value)) ? 'pointer' : 'not-allowed',
                                background: isQtyTypeEditable(String(value)) ? 'white' : '#f3f4f6',
                                width: '100%',
                                maxWidth: '200px',
                                height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                              }}
                            >
                              {getConditionalQuantityTypes(String(value)).map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </select>
                          ) : column === 'Storage' ? (
                            <textarea
                              ref={(el) => { fieldRefs.current[`${rowIndex}-${column}`] = el; }}
                              value={String(value)}
                              onChange={(e) => {
                                handleCellChange(rowIndex, column, e.target.value);
                                autoResizeTextarea(e.target, rowIndex, updateRowHeight);
                              }}
                              placeholder="Enter Storage Location"
                              rows={1}
                              style={{
                                ...inputStyle,
                                cursor: 'text',
                                background: 'white',
                                width: '100%',
                                maxWidth: '200px',
                                resize: 'none',
                                overflow: 'hidden',
                                minHeight: '48px',
                                maxHeight: '200px',
                                fontFamily: 'inherit',
                                lineHeight: '1.5',
                                height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                              }}
                              onFocus={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#ef4444';
                                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                                }
                                autoResizeTextarea(e.currentTarget, rowIndex, updateRowHeight);
                              }}
                              onBlur={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                              }}
                              onInput={(e) => {
                                autoResizeTextarea(e.currentTarget as HTMLTextAreaElement, rowIndex, updateRowHeight);
                              }}
                            />
                          ) : column === 'Expiry Date' ? (
                            <input
                              ref={(el) => { fieldRefs.current[`${rowIndex}-${column}`] = el; }}
                              type="date"
                              value={String(value)}
                              onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                              style={{
                                ...inputStyle,
                                background: 'white',
                                width: '100%',
                                maxWidth: '200px',
                                height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                              }}
                            />
                          ) : column === 'Medicine Name' ? (
                            <div style={{ 
                              position: 'relative', 
                              width: '100%',
                              maxWidth: '200px',
                              zIndex: (showDropdown[rowIndex] || isSearching[rowIndex]) ? 1000 : 1
                            }} ref={(el) => { dropdownRefs.current[rowIndex] = el; }}>
                              <textarea
                                ref={(el) => { 
                                  inputRefs.current[rowIndex] = el;
                                  fieldRefs.current[`${rowIndex}-Medicine Name`] = el;
                                }}
                                value={value}
                                onChange={(e) => {
                                  handleMedicineNameChange(rowIndex, e.target.value);
                                  autoResizeTextarea(e.target, rowIndex, updateRowHeight);
                                }}
                                placeholder="Enter Medicine Name"
                                rows={1}
                                style={{
                                  ...inputStyle,
                                  cursor: 'text',
                                  position: 'relative',
                                  zIndex: (showDropdown[rowIndex] || isSearching[rowIndex]) ? 10 : 1,
                                  width: '100%',
                                  boxSizing: 'border-box',
                                  resize: 'none',
                                  overflow: 'hidden',
                                  minHeight: '48px',
                                  maxHeight: '200px',
                                  fontFamily: 'inherit',
                                  lineHeight: '1.5',
                                  height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                                }}
                                onInput={(e) => {
                                  autoResizeTextarea(e.currentTarget as HTMLTextAreaElement, rowIndex, updateRowHeight);
                                }}
                                onFocus={(e) => {
                                  if (e.currentTarget) {
                                    e.currentTarget.style.borderColor = '#ef4444';
                                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                                  }
                                  const currentValue = rows[rowIndex]?.['Medicine Name'] || '';
                                  const trimmed = String(currentValue).trim();
                                  if (trimmed.length > 0) {
                                    if (searchTimeoutRef.current[rowIndex]) {
                                      clearTimeout(searchTimeoutRef.current[rowIndex] as unknown as number);
                                      searchTimeoutRef.current[rowIndex] = null;
                                    }
                                    setActiveDropdownRow(rowIndex);
                                    setIsSearching(prev => ({
                                      ...prev,
                                      [rowIndex]: true
                                    }));
                                    setShowDropdown(prev => ({
                                      ...prev,
                                      [rowIndex]: true
                                    }));
                                    (async () => {
                                      const suggestions = await searchMedicines(trimmed, rowIndex);
                                      setMedicineSuggestions(prev => ({
                                        ...prev,
                                        [rowIndex]: suggestions
                                      }));
                                      setIsSearching(prev => ({
                                        ...prev,
                                        [rowIndex]: false
                                      }));
                                      setShowDropdown(prev => ({
                                        ...prev,
                                        [rowIndex]: true
                                      }));
                                      setActiveDropdownRow(rowIndex);
                                    })();
                                  }
                                }}
                                onBlur={(e) => {
                                  const target = e.currentTarget;
                                  setTimeout(() => {
                                    if (target) {
                                      target.style.borderColor = '#d1d5db';
                                      target.style.boxShadow = 'none';
                                    }
                                  }, 200);
                                }}
                              />
                              {/* Mobile Dropdown - Positioned below Medicine Name input */}
                              {activeDropdownRow === rowIndex && (showDropdown[rowIndex] || isSearching[rowIndex]) && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 4px)',
                                    left: 0,
                                    right: 0,
                                    width: '100%',
                                    background: 'white',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    zIndex: 1001,
                                    maxHeight: '270px',
                                    overflowY: 'auto'
                                  }}
                                >
                                  {isSearching[rowIndex] ? (
                                    <div style={{
                                      padding: '1.5rem',
                                      textAlign: 'center',
                                      color: '#6b7280',
                                      fontSize: '0.875rem'
                                    }}>
                                      Searching...
                                    </div>
                                  ) : medicineSuggestions[rowIndex] && medicineSuggestions[rowIndex].length > 0 ? (
                                    medicineSuggestions[rowIndex].map((medicine, medIndex) => (
                                    <div
                                      key={medicine.key}
                                      data-dropdown-item
                                      onClick={() => handleMedicineSelect(rowIndex, medicine)}
                                      style={{
                                        padding: '0.75rem 1rem',
                                        cursor: 'pointer',
                                        borderBottom: medIndex < medicineSuggestions[rowIndex].length - 1 ? '1px solid #f3f4f6' : 'none',
                                        transition: 'background-color 0.15s ease',
                                        background: 'white'
                                      }}
                                      onTouchStart={(e) => {
                                        if (e.currentTarget) {
                                          e.currentTarget.style.backgroundColor = '#fef2f2';
                                        }
                                      }}
                                      onTouchEnd={(e) => {
                                        if (e.currentTarget) {
                                          e.currentTarget.style.backgroundColor = 'white';
                                        }
                                      }}
                                    >
                                      <div style={{
                                        fontWeight: '600',
                                        color: '#1f2937',
                                        fontSize: '0.875rem',
                                        marginBottom: '0.5rem'
                                      }}>
                                        {medicine.Name}
                                      </div>
                                      <div style={{
                                        fontSize: '0.75rem',
                                        color: '#6b7280',
                                        marginBottom: '0.25rem'
                                      }}>
                                        <strong>Company:</strong> {medicine.Manufacturer}
                                      </div>
                                      <div style={{
                                        fontSize: '0.75rem',
                                        color: '#6b7280',
                                        marginBottom: '0.25rem'
                                      }}>
                                        <strong>Composition:</strong> {medicine.Salt_Composition}
                                      </div>
                                      {(medicine.quantity || medicine.qty) && (
                                        <div style={{
                                          fontSize: '0.75rem',
                                          color: '#6b7280'
                                        }}>
                                          <strong>Quantity:</strong> {medicine.quantity || medicine.qty} {normalizeQtyType(medicine.quantity_type)}
                                          {medicine.tablets_per_strip && Number(medicine.tablets_per_strip) > 0 && (
                                            <span> ({Number(medicine.quantity || medicine.qty || 0) * Number(medicine.tablets_per_strip)} Tablets)</span>
                                          )}
                                        </div>
                                      )}
                                      <div style={{
                                        fontSize: '0.75rem',
                                        color: '#6b7280'
                                      }}>
                                        <strong>Fragmented Tablets:</strong> {medicine.fragmented_tablets ?? 0}
                                      </div>
                                    </div>
                                  ))
                                  ) : (
                                    <div style={{
                                      padding: '1.5rem',
                                      textAlign: 'center',
                                      color: '#6b7280',
                                      fontSize: '0.875rem'
                                    }}>
                                      No medicines found
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : column === 'Batch No' ? (
                            <textarea
                              ref={(el) => { fieldRefs.current[`${rowIndex}-${column}`] = el; }}
                              value={String(value)}
                              onChange={(e) => {
                                handleCellChange(rowIndex, column, e.target.value);
                                autoResizeTextarea(e.target, rowIndex, updateRowHeight);
                              }}
                              placeholder={`Enter ${column}`}
                              readOnly={isReadOnly}
                              rows={1}
                              style={{
                                ...inputStyle,
                                cursor: isReadOnly ? 'not-allowed' : 'text',
                                width: '100%',
                                maxWidth: '200px',
                                resize: 'none',
                                overflow: 'hidden',
                                minHeight: '48px',
                                maxHeight: '200px',
                                fontFamily: 'inherit',
                                lineHeight: '1.5',
                                textAlign: isRightAligned ? 'right' : 'left',
                                height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                              }}
                              onFocus={(e) => {
                                if (!isReadOnly && e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#ef4444';
                                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                                }
                                autoResizeTextarea(e.currentTarget, rowIndex, updateRowHeight);
                              }}
                              onBlur={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                              }}
                              onInput={(e) => {
                                autoResizeTextarea(e.currentTarget as HTMLTextAreaElement, rowIndex, updateRowHeight);
                              }}
                            />
                      ) : NUMERIC_EDITABLE_FIELDS.includes(column) ? (
                        <input
                          ref={(el) => { fieldRefs.current[`${rowIndex}-${column}`] = el; }}
                          type="text"
                          inputMode="decimal"
                          value={
                            numericDisplayValues[`${rowIndex}-${column}`] !== undefined
                              ? numericDisplayValues[`${rowIndex}-${column}`]
                              : value === 0 ? '' : String(value)
                          }
                          onChange={(e) => {
                            const filteredValue = handleDecimalInput(e.target.value);
                            handleCellChange(rowIndex, column, filteredValue);
                          }}
                          placeholder={`Enter ${column}`}
                          readOnly={isReadOnly}
                          style={{
                            ...inputStyle,
                            cursor: isReadOnly ? 'not-allowed' : 'text',
                            width: '100%',
                            maxWidth: '200px',
                            textAlign: isRightAligned ? 'right' : 'left',
                            height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                          }}
                          onFocus={(e) => {
                            if (!isReadOnly && e.currentTarget) {
                              e.currentTarget.style.borderColor = '#ef4444';
                              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                            }
                          }}
                          onBlur={(e) => {
                            if (e.currentTarget) {
                              e.currentTarget.style.borderColor = '#d1d5db';
                              e.currentTarget.style.boxShadow = 'none';
                            }
                            const fieldKey = `${rowIndex}-${column}`;
                            setNumericDisplayValues(prev => {
                              if (prev[fieldKey] === undefined) return prev;
                              const newValues = { ...prev };
                              delete newValues[fieldKey];
                              return newValues;
                            });
                          }}
                        />
                          ) : NUMERIC_EDITABLE_FIELDS.includes(column) ? (
                            <input
                              ref={(el) => { fieldRefs.current[`${rowIndex}-${column}`] = el; }}
                              type="text"
                              inputMode="decimal"
                              value={
                                numericDisplayValues[`${rowIndex}-${column}`] !== undefined
                                  ? numericDisplayValues[`${rowIndex}-${column}`]
                                  : value === 0 ? '' : String(value)
                              }
                              onChange={(e) => {
                                const filteredValue = handleDecimalInput(e.target.value);
                                handleCellChange(rowIndex, column, filteredValue);
                              }}
                              placeholder={`Enter ${column}`}
                              readOnly={isReadOnly}
                              style={{
                                ...inputStyle,
                                cursor: isReadOnly ? 'not-allowed' : 'text',
                                width: '100%',
                                maxWidth: '200px',
                                textAlign: isRightAligned ? 'right' : 'left',
                                height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                              }}
                              onFocus={(e) => {
                                if (!isReadOnly && e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#ef4444';
                                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                                }
                              }}
                              onBlur={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                                const fieldKey = `${rowIndex}-${column}`;
                                setNumericDisplayValues(prev => {
                                  if (prev[fieldKey] === undefined) return prev;
                                  const newValues = { ...prev };
                                  delete newValues[fieldKey];
                                  return newValues;
                                });
                              }}
                            />
                          ) : (
                            <input
                              ref={(el) => { fieldRefs.current[`${rowIndex}-${column}`] = el; }}
                              type="text"
                              value={value}
                              onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                              placeholder={`Enter ${column}`}
                              readOnly={isReadOnly}
                              style={{
                                ...inputStyle,
                                cursor: isReadOnly ? 'not-allowed' : 'text',
                                width: '100%',
                                maxWidth: '200px',
                                textAlign: isRightAligned ? 'right' : 'left',
                                height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                              }}
                              onFocus={(e) => {
                                if (!isReadOnly && e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#ef4444';
                                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                                }
                              }}
                              onBlur={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                              }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Remove and Add Item Buttons */}
                <div style={{
                  marginTop: '1rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={() => handleRemoveRow(rowIndex)}
                    style={{
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.625rem 1.25rem',
                      fontSize: '0.8125rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)',
                      flex: '1 1 45%',
                      minWidth: '120px'
                    }}
                    onTouchStart={(e) => {
                      if (e.currentTarget) {
                        e.currentTarget.style.transform = 'scale(0.98)';
                      }
                    }}
                    onTouchEnd={(e) => {
                      if (e.currentTarget) {
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      <line x1="10" y1="11" x2="10" y2="17"/>
                      <line x1="14" y1="11" x2="14" y2="17"/>
                    </svg>
                    Remove Item
                  </button>
                  <button
                    onClick={handleAddField}
                    style={{
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.625rem 1.25rem',
                      fontSize: '0.8125rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)',
                      flex: '1 1 45%',
                      minWidth: '120px'
                    }}
                    onTouchStart={(e) => {
                      if (e.currentTarget) {
                        e.currentTarget.style.transform = 'scale(0.98)';
                      }
                    }}
                    onTouchEnd={(e) => {
                      if (e.currentTarget) {
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Add Item
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        /* Desktop Table Layout */
        <div 
          ref={tableContainerRef}
          className="billing-table-scroll-container"
          style={{
            border: '2px solid #e5e7eb',
            borderRadius: '0',
            overflowX: 'scroll',
            overflowY: 'visible',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            background: 'white',
            margin: 0,
            padding: 0,
            paddingBottom: activeDropdownRow !== null && showDropdown[activeDropdownRow] && dropdownHeight > 0
              ? `${dropdownHeight + 10}px`
              : '0',
            marginLeft: 0,
            marginRight: 0,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            minHeight: 'fit-content',
            position: 'relative',
            transition: 'padding-bottom 0.2s ease'
          }}
        >
        <div 
          ref={tableScrollableRef}
          style={{
            display: 'block',
            width: 'max-content',
            minWidth: '100%'
          }}>
          <table style={{
            borderCollapse: 'collapse',
            fontSize: '0.875rem',
            margin: 0,
            padding: 0,
            borderSpacing: 0,
            width: 'max-content',
            minWidth: '100%',
            tableLayout: 'auto'
          }}>
            <thead>
              <tr style={{
                background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                borderBottom: '2px solid #d1d5db'
              }}>
                {displayColumns.map((column, index) => (
                  <th
                    key={column}
                    style={{
                      padding: index === 0 ? '1rem 0.75rem 1rem 0' : '1rem 0.75rem',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '0.8125rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                      whiteSpace: 'nowrap',
                      minWidth: '120px',
                      margin: 0
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem' }}>
                      <span>{column}</span>
                    </div>
                  </th>
                ))}
                <th
                  style={{
                    padding: '1rem 0.75rem',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '0.8125rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                    whiteSpace: 'nowrap',
                    minWidth: '100px'
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={displayColumns.length + 1}
                    style={{
                      padding: '3rem 1rem',
                      textAlign: 'center',
                      color: '#6b7280'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}>
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: '#6b7280' }}>
                        No rows added yet
                      </p>
                      <p style={{ margin: 0, fontSize: '0.875rem', color: '#9ca3af' }}>
                        Click "Add Field" to start adding billing items
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    style={{
                      borderBottom: '1px solid #e5e7eb',
                      background: rowIndex % 2 === 1 ? '#f9fafb' : 'white',
                      transition: 'background-color 0.15s ease',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      if (e.currentTarget) {
                        e.currentTarget.style.backgroundColor = '#fef2f2';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (e.currentTarget) {
                        e.currentTarget.style.backgroundColor = rowIndex % 2 === 1 ? '#f9fafb' : 'white';
                      }
                    }}
                  >
                    {displayColumns.map((column, colIndex) => {
                      const value = row[column];
                      const isRightAligned = rightAlignColumns.includes(column);
                      const isReadOnly = column === 'S. No' || column === 'Total' || column === 'Total Tablets';
                      // GST% is now editable

                      const inputStyle: React.CSSProperties = {
                        width: '100%',
                        padding: colIndex === 0 ? '0.5rem 0.75rem 0.5rem 0' : '0.5rem 0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        background: isReadOnly ? '#f3f4f6' : 'white',
                        color: '#111827',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        minWidth: '120px',
                        boxSizing: 'border-box',
                        margin: 0
                      };

                      return (
                        <td
                          key={column}
                          style={{
                            padding: colIndex === 0 ? '0.75rem 0.75rem 0.75rem 0' : '0.75rem',
                            textAlign: isRightAligned ? 'right' : 'left',
                            borderRight: '1px solid #f3f4f6',
                            verticalAlign: 'top',
                            margin: 0,
                            position: column === 'Medicine Name' ? 'relative' : 'static',
                            overflow: column === 'Medicine Name' ? 'visible' : 'hidden'
                          }}
                        >
                          {column === 'S. No' ? (
                            <span style={{
                              fontWeight: '600',
                              color: '#4b5563',
                              background: '#f3f4f6',
                              padding: '0.25rem 0.5rem 0.25rem 0',
                              borderRadius: '8px',
                              display: 'inline-block',
                              minWidth: '40px',
                              textAlign: 'center',
                              margin: 0
                            }}>
                              {value}
                            </span>
                          ) : column === 'Total' ? (
                            <span style={{
                              fontWeight: '700',
                              color: '#ef4444',
                              fontSize: '0.9375rem'
                            }}>
                              {formatCurrency(Number(value))}
                            </span>
                          ) : column === 'Total Tablets' ? (
                            <span style={{
                              fontWeight: '700',
                              color: '#10b981',
                              fontSize: '0.9375rem'
                            }}>
                              {value}
                            </span>
                          ) : column === 'Tablets/Strip' ? (
                            <span style={{
                              fontWeight: '600',
                              color: '#374151',
                              fontSize: '0.875rem'
                            }}>
                              {value || 0}
                            </span>
                          ) : column === 'Qty Type' ? (
                            <select
                              value={String(value)}
                              onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                              disabled={!isQtyTypeEditable(String(value))}
                              style={{
                                ...inputStyle,
                                cursor: isQtyTypeEditable(String(value)) ? 'pointer' : 'not-allowed',
                                background: isQtyTypeEditable(String(value)) ? 'white' : '#f3f4f6'
                              }}
                            >
                              {getConditionalQuantityTypes(String(value)).map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </select>
                          ) : column === 'Storage' ? (
                            <input
                              type="text"
                              value={String(value)}
                              onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                              placeholder="Enter Storage Location"
                              style={{
                                ...inputStyle,
                                cursor: 'text',
                                background: 'white'
                              }}
                              onFocus={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#ef4444';
                                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                                }
                              }}
                              onBlur={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                              }}
                            />
                          ) : column === 'Expiry Date' ? (
                            <input
                              type="date"
                              value={String(value)}
                              onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                              style={{
                                ...inputStyle,
                                minWidth: '150px',
                                background: 'white'
                              }}
                            />
                          ) : column === 'Medicine Name' ? (
                            <div style={{ 
                              position: 'relative', 
                              width: '100%',
                              zIndex: (showDropdown[rowIndex] || isSearching[rowIndex]) ? 1000 : 1
                            }} ref={(el) => { dropdownRefs.current[rowIndex] = el; }}>
                              <textarea
                                ref={(el) => { 
                                  inputRefs.current[rowIndex] = el;
                                  fieldRefs.current[`${rowIndex}-Medicine Name`] = el;
                                }}
                                value={value}
                                onChange={(e) => {
                                  handleMedicineNameChange(rowIndex, e.target.value);
                                  autoResizeTextarea(e.target, rowIndex, updateRowHeight);
                                }}
                                placeholder="Enter Medicine Name"
                                rows={1}
                                style={{
                                  ...inputStyle,
                                  cursor: 'text',
                                  position: 'relative',
                                  zIndex: (showDropdown[rowIndex] || isSearching[rowIndex]) ? 10 : 1,
                                  width: '100%',
                                  boxSizing: 'border-box',
                                  resize: 'none',
                                  overflow: 'hidden',
                                  minHeight: '48px',
                                  maxHeight: '200px',
                                  fontFamily: 'inherit',
                                  lineHeight: '1.5',
                                  height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                                }}
                                onInput={(e) => {
                                  autoResizeTextarea(e.currentTarget as HTMLTextAreaElement, rowIndex, updateRowHeight);
                                }}
                                onFocus={(e) => {
                                  if (e.currentTarget) {
                                    e.currentTarget.style.borderColor = '#ef4444';
                                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                                  }
                                  const currentValue = rows[rowIndex]?.['Medicine Name'] || '';
                                  const trimmed = String(currentValue).trim();
                                  if (trimmed.length > 0) {
                                    if (searchTimeoutRef.current[rowIndex]) {
                                      clearTimeout(searchTimeoutRef.current[rowIndex] as unknown as number);
                                      searchTimeoutRef.current[rowIndex] = null;
                                    }
                                    setActiveDropdownRow(rowIndex);
                                    setIsSearching(prev => ({
                                      ...prev,
                                      [rowIndex]: true
                                    }));
                                    setShowDropdown(prev => ({
                                      ...prev,
                                      [rowIndex]: true
                                    }));
                                    (async () => {
                                      const suggestions = await searchMedicines(trimmed, rowIndex);
                                      setMedicineSuggestions(prev => ({
                                        ...prev,
                                        [rowIndex]: suggestions
                                      }));
                                      setIsSearching(prev => ({
                                        ...prev,
                                        [rowIndex]: false
                                      }));
                                      setShowDropdown(prev => ({
                                        ...prev,
                                        [rowIndex]: true
                                      }));
                                      setActiveDropdownRow(rowIndex);
                                    })();
                                  }
                                }}
                                onBlur={(e) => {
                                  const target = e.currentTarget;
                                  setTimeout(() => {
                                    if (target) {
                                      target.style.borderColor = '#d1d5db';
                                      target.style.boxShadow = 'none';
                                    }
                                  }, 200);
                                }}
                              />
                            </div>
                          ) : column === 'Batch No' ? (
                            <textarea
                              ref={(el) => { fieldRefs.current[`${rowIndex}-${column}`] = el; }}
                              value={String(value)}
                              onChange={(e) => {
                                handleCellChange(rowIndex, column, e.target.value);
                                autoResizeTextarea(e.target, rowIndex, updateRowHeight);
                              }}
                              placeholder={`Enter ${column}`}
                              readOnly={isReadOnly}
                              rows={1}
                              style={{
                                ...inputStyle,
                                cursor: isReadOnly ? 'not-allowed' : 'text',
                                resize: 'none',
                                overflow: 'hidden',
                                minHeight: '48px',
                                maxHeight: '200px',
                                fontFamily: 'inherit',
                                lineHeight: '1.5',
                                height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                              }}
                              onFocus={(e) => {
                                if (!isReadOnly && e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#ef4444';
                                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                                }
                                autoResizeTextarea(e.currentTarget, rowIndex, updateRowHeight);
                              }}
                              onBlur={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                              }}
                              onInput={(e) => {
                                autoResizeTextarea(e.currentTarget as HTMLTextAreaElement, rowIndex, updateRowHeight);
                              }}
                            />
                          ) : NUMERIC_EDITABLE_FIELDS.includes(column) ? (
                            <input
                              ref={(el) => { fieldRefs.current[`${rowIndex}-${column}`] = el; }}
                              type="text"
                              inputMode="decimal"
                              value={
                                numericDisplayValues[`${rowIndex}-${column}`] !== undefined
                                  ? numericDisplayValues[`${rowIndex}-${column}`]
                                  : value === 0 ? '' : String(value)
                              }
                              onChange={(e) => {
                                const filteredValue = handleDecimalInput(e.target.value);
                                handleCellChange(rowIndex, column, filteredValue);
                              }}
                              placeholder={`Enter ${column}`}
                              readOnly={isReadOnly}
                              style={{
                                ...inputStyle,
                                cursor: isReadOnly ? 'not-allowed' : 'text',
                                height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                              }}
                              onFocus={(e) => {
                                if (!isReadOnly && e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#ef4444';
                                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                                }
                              }}
                              onBlur={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                                const fieldKey = `${rowIndex}-${column}`;
                                setNumericDisplayValues(prev => {
                                  if (prev[fieldKey] === undefined) return prev;
                                  const newValues = { ...prev };
                                  delete newValues[fieldKey];
                                  return newValues;
                                });
                              }}
                            />
                          ) : (
                            <input
                              ref={(el) => { fieldRefs.current[`${rowIndex}-${column}`] = el; }}
                              type="text"
                              value={value}
                              onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                              placeholder={`Enter ${column}`}
                              readOnly={isReadOnly}
                              style={{
                                ...inputStyle,
                                cursor: isReadOnly ? 'not-allowed' : 'text',
                                height: rowHeights[rowIndex] ? `${rowHeights[rowIndex]}px` : '48px'
                              }}
                              onFocus={(e) => {
                                if (!isReadOnly && e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#ef4444';
                                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                                }
                              }}
                              onBlur={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                              }}
                            />
                          )}
                        </td>
                      );
                    })}
                    {/* Actions Column - Delete Button */}
                    <td
                      style={{
                        padding: '0.75rem',
                        textAlign: 'center',
                        borderRight: '1px solid #f3f4f6',
                        verticalAlign: 'middle'
                      }}
                    >
                      <button
                        onClick={() => handleRemoveRow(rowIndex)}
                        style={{
                          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.5rem 0.75rem',
                          fontSize: '0.8125rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.375rem',
                          boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)',
                          minWidth: '90px',
                          margin: '0 auto'
                        }}
                        onMouseEnter={(e) => {
                          if (e.currentTarget) {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (e.currentTarget) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.2)';
                          }
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          <line x1="10" y1="11" x2="10" y2="17"/>
                          <line x1="14" y1="11" x2="14" y2="17"/>
                        </svg>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Full-width Dropdown - Positioned relative to table container */}
        {activeDropdownRow !== null && (showDropdown[activeDropdownRow] || isSearching[activeDropdownRow]) && (
          <div
            ref={(el) => { if (activeDropdownRow !== null) dropdownRefs.current[activeDropdownRow] = el; }}
            style={{
              position: 'absolute',
              top: inputRefs.current[activeDropdownRow] && tableContainerRef.current
                ? `${inputRefs.current[activeDropdownRow]!.getBoundingClientRect().bottom - tableContainerRef.current.getBoundingClientRect().top + 2}px`
                : 'auto',
              left: 0,
              right: 0,
              width: '100%',
              background: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '0',
              boxShadow: 'none',
              zIndex: 1001,
              height: '270px', // Fixed height to show exactly 3 items (90px per item)
              overflowY: 'auto'
            }}
          >
            {isSearching[activeDropdownRow] ? (
              <div style={{
                padding: '1.5rem',
                textAlign: 'center',
                color: '#6b7280',
                fontSize: '0.875rem'
              }}>
                Searching...
              </div>
            ) : medicineSuggestions[activeDropdownRow] && medicineSuggestions[activeDropdownRow].length > 0 ? (
              medicineSuggestions[activeDropdownRow].map((medicine, medIndex) => (
              <div
                key={medicine.key}
                data-dropdown-item
                onClick={() => handleMedicineSelect(activeDropdownRow, medicine)}
                style={{
                  padding: '0.75rem 1rem',
                  cursor: 'pointer',
                  borderBottom: medIndex < medicineSuggestions[activeDropdownRow].length - 1 ? '1px solid #f3f4f6' : 'none',
                  transition: 'background-color 0.15s ease',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '2rem',
                  flexWrap: 'wrap'
                }}
                onMouseEnter={(e) => {
                  if (e.currentTarget) {
                    e.currentTarget.style.backgroundColor = '#fef2f2';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.currentTarget) {
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                {/* Medicine Name */}
                <div style={{
                  flex: '1 1 200px',
                  minWidth: '150px'
                }}>
                  <div style={{
                    fontWeight: '600',
                    color: '#1f2937',
                    fontSize: '0.875rem',
                    marginBottom: '0.25rem'
                  }}>
                    {medicine.Name}
                  </div>
                </div>
                
                {/* Company */}
                <div style={{
                  flex: '1 1 200px',
                  minWidth: '150px'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontWeight: '500',
                    marginBottom: '0.125rem'
                  }}>
                    <strong>Company:</strong>
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#374151'
                  }}>
                    {medicine.Manufacturer}
                  </div>
                </div>
                
                {/* Composition */}
                <div style={{
                  flex: '1 1 250px',
                  minWidth: '200px'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontWeight: '500',
                    marginBottom: '0.125rem'
                  }}>
                    <strong>Composition:</strong>
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#374151'
                  }}>
                    {medicine.Salt_Composition}
                  </div>
                </div>
                
                {/* Quantity */}
                {(medicine.quantity || medicine.qty) && (
                  <div style={{
                    flex: '0 0 150px',
                    textAlign: 'right'
                  }}>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      fontWeight: '500',
                      marginBottom: '0.125rem'
                    }}>
                      <strong>Quantity:</strong>
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#374151',
                      fontWeight: '600'
                    }}>
                      {medicine.quantity || medicine.qty} {normalizeQtyType(medicine.quantity_type)}
                      {medicine.tablets_per_strip && Number(medicine.tablets_per_strip) > 0 && (
                        <div style={{
                          fontSize: '0.7rem',
                          color: '#6b7280',
                          fontWeight: '400',
                          marginTop: '0.125rem'
                        }}>
                          ({Number(medicine.quantity || medicine.qty || 0) * Number(medicine.tablets_per_strip)} Tablets)
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Fragmented Tablets */}
                <div style={{
                  flex: '0 0 150px',
                  textAlign: 'right'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontWeight: '500',
                    marginBottom: '0.125rem'
                  }}>
                    <strong>Fragmented:</strong>
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#374151',
                    fontWeight: '600'
                  }}>
                    {medicine.fragmented_tablets ?? 0}
                  </div>
                </div>
              </div>
            ))
            ) : (
              <div style={{
                padding: '1.5rem',
                textAlign: 'center',
                color: '#6b7280',
                fontSize: '0.875rem'
              }}>
                No medicines found
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Summary and Submit Button */}
      {rows.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: isMobile ? 'center' : 'space-between',
          alignItems: 'center',
          paddingTop: '0.5rem',
          flexWrap: 'wrap',
          gap: '1rem',
          width: '100%',
          paddingLeft: isMobile ? '0.5rem' : '0',
          paddingRight: isMobile ? '0.5rem' : '0'
        }}>
          {/* Summary Section */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem',
            width: isMobile ? '100%' : 'auto',
            alignItems: isMobile ? 'center' : 'flex-start'
          }}>
            <div style={{
              fontSize: isMobile ? '0.875rem' : '0.9375rem',
              fontWeight: '600',
              color: '#374151'
            }}>
              Total Items: <span style={{ color: '#ef4444', fontWeight: '700' }}>{rows.length}</span>
            </div>
            <div style={{
              fontSize: isMobile ? '0.875rem' : '0.9375rem',
              fontWeight: '600',
              color: '#374151'
            }}>
              Total Value: <span style={{ color: '#ef4444', fontWeight: '700' }}>{formatCurrency(calculateTotalValue())}</span>
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Thin modern horizontal scrollbar (overrides index.css global hide) */
        .billing-table-scroll-container {
          -ms-overflow-style: auto;
          scrollbar-width: thin;
          scrollbar-color: rgba(113, 113, 122, 0.55) rgba(24, 24, 27, 0.06);
        }
        .billing-table-scroll-container::-webkit-scrollbar {
          display: block;
          height: 6px;
          -webkit-appearance: none;
        }
        .billing-table-scroll-container::-webkit-scrollbar-track {
          background: rgba(24, 24, 27, 0.06);
          border-radius: 100px;
          margin: 0 6px;
        }
        .billing-table-scroll-container::-webkit-scrollbar-thumb {
          background: rgba(113, 113, 122, 0.45);
          border-radius: 100px;
          border: 2px solid transparent;
          background-clip: padding-box;
          transition: background-color 0.15s ease;
          min-width: 40px;
        }
        .billing-table-scroll-container::-webkit-scrollbar-thumb:hover {
          background-color: rgba(82, 82, 91, 0.6);
          background-clip: padding-box;
        }
        .billing-table-scroll-container::-webkit-scrollbar-thumb:active {
          background-color: rgba(63, 63, 70, 0.75);
          background-clip: padding-box;
        }
        .billing-table-scroll-container::-webkit-scrollbar-corner {
          background: transparent;
        }
      `}</style>
    </div>
  );
};

export default BillingTable;

